import { useMemo } from 'react'
import {
  startOfWeek, endOfWeek, eachDayOfInterval,
  format, isToday, isSameDay, parseISO,
} from 'date-fns'
import { cn, getEventColor } from '../../../lib/utils'

const HOUR_START = 6
const HOUR_END   = 23
const TOTAL_HRS  = HOUR_END - HOUR_START
const CELL_H     = 56  // px per hour

export const WeekView = ({ week, events, onEventClick, onDaySelect }) => {
  const days = useMemo(() => {
    return eachDayOfInterval({ start: startOfWeek(week), end: endOfWeek(week) })
  }, [week])

  const hours = Array.from({ length: TOTAL_HRS }, (_, i) => HOUR_START + i)

  const positioned = useMemo(() => {
    return events
      .filter((e) => !e.is_all_day && e.start_time)
      .map((e) => {
        const start  = parseISO(e.start_time)
        const end    = e.end_time ? parseISO(e.end_time) : new Date(start.getTime() + 60 * 60 * 1000)
        const dayIdx = days.findIndex((d) => isSameDay(d, start))
        if (dayIdx === -1) return null

        const startMin = start.getHours() * 60 + start.getMinutes()
        const endMin   = end.getHours() * 60 + end.getMinutes()
        const top      = ((startMin / 60 - HOUR_START) / TOTAL_HRS) * (TOTAL_HRS * CELL_H)
        const height   = Math.max(((endMin - startMin) / 60 / TOTAL_HRS) * (TOTAL_HRS * CELL_H), 20)
        return { ...e, dayIdx, top, height }
      })
      .filter(Boolean)
  }, [events, days])

  return (
    <div className="flex-1 overflow-auto">
      {/* Day headers */}
      <div className="flex sticky top-0 z-10 bg-surface border-b border-border">
        <div className="w-12 flex-shrink-0" />
        {days.map((day) => (
          <button
            key={format(day, 'yyyy-MM-dd')}
            onClick={() => onDaySelect(day)}
            className="flex-1 text-center py-2 hover:bg-background transition-colors"
          >
            <div className="text-[11px] text-text-muted">{format(day, 'EEE')}</div>
            <div
              className={cn(
                'text-sm font-semibold mx-auto w-7 h-7 flex items-center justify-center rounded-full',
                isToday(day) ? 'bg-primary text-white' : 'text-text-primary',
              )}
            >
              {format(day, 'd')}
            </div>
          </button>
        ))}
      </div>

      {/* Time grid */}
      <div className="flex">
        {/* Hour labels */}
        <div className="w-12 flex-shrink-0">
          {hours.map((h) => (
            <div
              key={h}
              className="text-[10px] text-text-muted text-right pr-2 leading-none"
              style={{ height: CELL_H, paddingTop: 2 }}
            >
              {h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div className="flex-1 relative grid grid-cols-7">
          {days.map((_, idx) => (
            <div
              key={idx}
              className="border-l border-border"
              style={{ height: TOTAL_HRS * CELL_H }}
            >
              {hours.map((h) => (
                <div
                  key={h}
                  className="border-t border-border/50"
                  style={{ height: CELL_H }}
                />
              ))}
            </div>
          ))}

          {/* Events overlay */}
          {positioned.map((e) => (
            <button
              key={e.id}
              onClick={() => onEventClick(e)}
              className="absolute text-left px-1 py-0.5 rounded text-[11px] font-medium text-white overflow-hidden leading-tight"
              style={{
                left:   `calc(${(e.dayIdx / 7) * 100}% + 2px)`,
                width:  `calc(${(1 / 7) * 100}% - 4px)`,
                top:    e.top,
                height: e.height,
                backgroundColor: getEventColor(e),
              }}
            >
              {e.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
